import { createSelector } from 'reselect';
import _sortBy from 'lodash/sortBy';
import parseISO from 'date-fns/parseISO';
import { adjustDateForTimezone } from 'helpers/intl-fns';
import {
  instructorCalendars as instructorCalendarsEntity,
  calendarConfigurationPlatforms as calendarConfigurationPlatformsEntity,
  googleCalendars as googleCalendarsEntity,
  staffAndEquipment as staffAndEquipmentEntity,
} from 'store/saga/entities/bound';
import { selectActiveVenue, selectActiveVenueId, selectVenueTimezone } from 'selectors/general';

const selectInstructorsCalendarEntity = createSelector(
  state => state,
  selectActiveVenueId,
  (state, activeVenueId) => instructorCalendarsEntity.select(state, activeVenueId)
);

export const selectInstructorCalendars = createSelector(
  selectInstructorsCalendarEntity,
  selectVenueTimezone,
  ({ data, error, loading, loaded }, timezone) => {
    if (!loaded) {
      return {
        data: {},
        error,
        isLoaded: loaded,
        isLoading: loading,
      };
    }

    const practitioners = data.practitioners.map(({ calendars, ...restPractitioner }) => ({
      ...restPractitioner,
      calendars: calendars.map(({ lastSyncSuccessTimestamp, ...restCalendar }) => ({
        ...restCalendar,
        lastSyncSuccessTimestamp:
          lastSyncSuccessTimestamp && adjustDateForTimezone(parseISO(lastSyncSuccessTimestamp), timezone),
      })),
    }));

    return {
      data: !loaded
        ? []
        : {
            ...data,
            practitioners,
          },
      error,
      isLoaded: loaded,
      isLoading: loading,
    };
  }
);

const selectCalendarConfigurationPlatformsEntity = createSelector(
  state => state,
  state => calendarConfigurationPlatformsEntity.select(state)
);

export const selectCalendarConfigurationPlatforms = createSelector(
  selectCalendarConfigurationPlatformsEntity,
  ({ data, error, loading, loaded }) => ({
    data: !loaded ? [] : _sortBy(data.calendarConfigurationPlatforms, 'name'),
    error,
    isLoaded: loaded,
    isLoading: loading,
  })
);

export const selectActiveVenueCalendarConfigurationPlatform = createSelector(
  selectCalendarConfigurationPlatforms,
  selectActiveVenue,
  ({ data: calendarConfigurationPlatforms, isLoaded }, { calendarConfigurationPlatform }) => {
    const data = calendarConfigurationPlatforms.find(({ name }) => name === calendarConfigurationPlatform) || {};
    return {
      ...data,
      isLoaded,
    };
  }
);

const selectGoogleCalendarsEntity = createSelector(
  state => state,
  selectActiveVenueId,
  (state, venueId) => googleCalendarsEntity.select(state, venueId)
);

export const selectGoogleCalendars = createSelector(
  selectGoogleCalendarsEntity,
  ({ data, error, loading, loaded }) => ({
    data: !loaded ? [] : data,
    error,
    isLoaded: loaded,
    isLoading: loading,
  })
);

const selectStaffAndEquipmentEntity = createSelector(
  (state, staffId) => ({ state, staffId } || {}),
  ({ state, staffId }) => {
    return staffAndEquipmentEntity.select(state, staffId);
  }
);

export const selectStaffAndEquipmentData = createSelector(
  selectStaffAndEquipmentEntity,
  ({ data = {}, loading, loaded, error }) => ({
    data,
    isLoading: loading,
    isLoaded: loaded,
    hasError: !!error,
    error,
  })
);

export const selectBookableTypeAndId = createSelector(selectStaffAndEquipmentData, ({ data: { bookableType, id } }) => {
  return {
    bookableType,
    id,
  };
});
